import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonItem,
  IonList,
  IonLoading,
  IonMenuButton,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  Box,
  Divider,
  FormControl,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { requestID, baseUrl, config, getToken } from "../../common/utils";
import {
  AccountBalance as AccountBalanceIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import "./LoanClearOff.css";

interface Loan {
  loan_id: string;
  user_id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  middleName: string;
  loanBal: string;
  loanRepayAmount: string;
  loan_amount: string;
  status?: string;
}

// Debug function
const debugAPI = async () => {
  console.log('=== API DEBUG INFO ===');
  console.log('Base URL:', baseUrl());

  const token = getToken();
  console.log('Token:', token);
  console.log('Token exists:', !!token);
  console.log('Request ID:', requestID());

  // Test the API endpoint directly
  try {
    const testFormData = new FormData();
    testFormData.append("requestID", requestID());
    console.log('Testing API endpoint...');

    // Use a fresh config to ensure token is current
    const testConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "multipart/form-data",
      },
    };
    
    const response = await axios.post(
      baseUrl() + "applyloans/getAllloans", 
      testFormData, 
      testConfig
    );
    console.log('API Response Status:', response.status);
    console.log('API Response Data:', response.data);
  } catch (error: any) {
    console.error('API Error:', error);
    if (error.response) {
      console.error('Error Status:', error.response.status);
      console.error('Error Data:', error.response.data);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error Message:', error.message);
    }
  }
};

const LoanClearOff = () => {
  const { name } = useParams<{ name: string }>();
  const theme = useTheme();
  const history = useHistory();

  const [status, setStatus] = useState({ loading: false, error: false });
  const [errorLogin, setErrorLogin] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [query, setQuery] = useState("");
  const [squery, setSquery] = useState("");
  const [allloans, setAllloans] = useState<Loan[]>([]);
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [loadCard, setLoadCard] = useState(false);

  const getAllloans = () => {
    const token = getToken(); // Get fresh token
    if (!token) {
      setErrorLogin("No authentication token found");
      callToast();
      setStatus({ loading: false, error: true });
      return;
    }

    const formData = new FormData();
    formData.append("requestID", requestID());
    
    // Create fresh config with current token
    const currentConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "multipart/form-data",
      },
    };
    
    setStatus({ loading: true, error: false });
    
    axios
      .post(baseUrl() + "applyloans/getAllloans", formData, currentConfig)
      .then((response) => {
        console.log("Loans response:", response);
setAllloans(
  (response.data.data || []).map((loan: any) => ({
    ...loan,
    loanBal: parseFloat((loan.loanBal || "0").toString().replace(/[^0-9.-]/g, "")),
  }))
);        setStatus({ loading: false, error: false });
      })
      .catch((error) => {
        console.error("Error fetching loans:", error);
        setStatus({ loading: false, error: true });
        setErrorLogin("Failed to load loans");
        callToast();
      });
  };

  const pushForApproval = () => {
    if (!selectedLoan) return;

    const token = getToken(); // Get fresh token
    if (!token) {
      setErrorLogin("No authentication token found");
      callToast();
      return;
    }

    setStatus({ loading: true, error: false });
    const formData = new FormData();

    formData.append("requestID", requestID());
    formData.append("loan_id", selectedLoan.loan_id);
    formData.append("user_id", selectedLoan.user_id);
    formData.append("fullName", selectedLoan.fullName);
    formData.append("loanBal", selectedLoan.loanBal);
    formData.append("loan_amount", selectedLoan.loan_amount);

    // Create fresh config with current token
    const currentConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
        "content-type": "multipart/form-data",
      },
    };

    axios
      .post(baseUrl() + "applyloans/pushLoanClearOffApproval", formData, currentConfig)
      .then((response) => {
        console.log("Approval response:", response);
        setStatus({ loading: false, error: false });
        
        if (response.data.status === 'false') {
          setErrorLogin('Failed to submit for approval!');
        } else {
          setErrorLogin('Successfully submitted for approval!');
          setSelectedLoan(null);
          setLoadCard(false);
          setSquery("");
        }
        callToast();
      })
      .catch((error) => {
        console.error("Approval error:", error);
        setStatus({ loading: false, error: true });
        setErrorLogin('Error submitting for approval');
        callToast();
      });
  };

  const callToast = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const queryEmpty = () => {
    setSquery("");
    setQuery("");
    setSelectedLoan(null);
    setLoadCard(false);
  };

  const queryKeep = (loan: Loan) => {
    setSquery(loan.fullName);
    setSelectedLoan(loan);
    setLoadCard(true);
    setQuery("");
  };

  const filteredLoans = allloans.filter((loan) =>
    loan.fullName?.toLowerCase().includes(query.toLowerCase())
  );

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      fontWeight: 'bold',
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(even)': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      cursor: 'pointer',
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  useIonViewWillEnter(() => {
    console.log('Component mounted - debugging API');
    debugAPI(); // Add this for debugging
    getAllloans();
  });

  return (
    <IonPage className="loan-clearoff-page pushDown">
      <IonHeader>
        <IonToolbar className="loan-header">
          <IonButtons slot="start">
            <IonMenuButton className="menu-button" />
          </IonButtons>
          <IonTitle>
            <div className="header-container">
              <div className="header-left">
                <AccountBalanceIcon className="header-icon" />
                <Typography variant="h6" className="coop-title">
               LOAN CLEAR OFF PAGE
                </Typography>
              </div>
            </div>
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="loan-content">
        <Box className="loan-container">
          {/* Page Header */}
          <Box className="page-header">
            <AccountBalanceIcon fontSize="large" className="page-icon" />
            <Typography variant="h4" component="h1" className="page-title">
              Loan Clear Off
            </Typography>
            <Typography variant="body1" className="page-subtitle">
              Manage and approve loan clearance requests
            </Typography>
          </Box>

          {/* Stats Overview */}
          <Grid container spacing={3} className="stats-grid">
            <Grid item xs={12} sm={4}>
              <Card className="stat-card total-members">
                <CardContent className="stat-content">
                  <PersonIcon className="stat-icon" />
                  <div>
                    <Typography variant="h4" className="stat-number">
                      {allloans.length}
                    </Typography>
                    <Typography variant="body2" className="stat-label">
                      Total Loans
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className="stat-card total-savings">
                <CardContent className="stat-content">
                  <MoneyIcon className="stat-icon" />
                  <div>
                    <Typography variant="h4" className="stat-number">
                      ₦{allloans.reduce((sum, loan) => sum + Number(loan.loanBal || 0), 0).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" className="stat-label">
                      Total Balance
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className="stat-card inconsistent-card">
                <CardContent className="stat-content">
                  <TrendingUpIcon className="stat-icon" />
                  <div>
                    <Typography variant="h4" className="stat-number">
                      {allloans.filter(loan => Number(loan.loanBal) > 0).length}
                    </Typography>
                    <Typography variant="body2" className="stat-label">
                      Active Loans
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search and Controls */}
          <Paper elevation={2} className="controls-paper">
            <Box className="controls-content">
              <FormControl fullWidth className="search-control">
                <IonSearchbar
                  className="custom-searchbar"
                  placeholder="Search active loans by member name..."
                  value={squery}
                  onIonClear={queryEmpty}
                  onIonInput={(e) => {
                    setQuery(e.detail.value!);
                    setSquery(e.detail.value!);
                  }}
                  animated={true}
                  mode="ios"
                />
              </FormControl>
              <IonButton 
                className="refresh-button"
                onClick={getAllloans}
                disabled={status.loading}
              >
                <RefreshIcon />
                Refresh
              </IonButton>
            </Box>
          </Paper>

          {/* Search Results */}
          {query && (
            <Paper elevation={2} className="results-paper">
              <Typography variant="subtitle2" className="results-title">
                Search Results ({filteredLoans.length})
              </Typography>
              <IonList className="suggestions-list">
                {filteredLoans.length > 0 ? (
                  filteredLoans.map((loan, index) => (
                    <IonItem
                      key={index}
                      lines="full"
                      className="suggestion-item"
                      onClick={() => queryKeep(loan)}
                    >
                      <PersonIcon fontSize="small" className="suggestion-icon" />
                      <Typography variant="body2" className="suggestion-text">
                        {loan.fullName}
                      </Typography>
                      <Chip
                        size="small"
                        label={`₦${Number(loan.loanBal || 0).toLocaleString()}`}
                        color="warning"
                        variant="outlined"
                        className="balance-chip"
                      />
                    </IonItem>
                  ))
                ) : (
                  <IonItem lines="none" className="no-results">
                    <Typography variant="body2" color="textSecondary">
                      No matching loans found
                    </Typography>
                  </IonItem>
                )}
              </IonList>
            </Paper>
          )}

          {/* Loan Details Card */}
          {loadCard && selectedLoan && (
            <Box className="loan-card-container">
              <Card elevation={3} className="loan-card">
                <CardContent className="card-content">
                  <Box className="card-header">
                    <CheckCircleIcon className="card-icon" />
                    <Typography variant="subtitle1" className="card-title">
                      SELECTED LOAN DETAILS
                    </Typography>
                  </Box>

                  <Box className="loan-details">
                    <Box className="detail-row member-row">
                      <PersonIcon fontSize="small" />
                      <Typography variant="body1" className="detail-text">
                        {selectedLoan.fullName}
                      </Typography>
                    </Box>

                    <Box className="detail-grid">
                      <Box className="detail-item balance-item">
                        <MoneyIcon fontSize="small" />
                        <Box>
                          <Typography variant="caption" className="detail-label">
                            Loan Balance
                          </Typography>
                          <Typography variant="h6" className="detail-value amount">
                            ₦{Number(selectedLoan.loanBal || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      <Box className="detail-item repayment-item">
                        <TrendingUpIcon fontSize="small" />
                        <Box>
                          <Typography variant="caption" className="detail-label">
                            Monthly Repayment
                          </Typography>
                          <Typography variant="h6" className="detail-value">
                            ₦{Number(selectedLoan.loanRepayAmount || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box className="action-section">
                      <IonButton
                        expand="block"
                        color="success"
                        onClick={pushForApproval}
                        className="approval-button"
                        size="small"
                      >
                        <CheckCircleIcon className="button-icon" />
                        Send for Clearance Approval
                      </IonButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          <IonLoading
            message="Processing..."
            spinner="circles"
            isOpen={status.loading}
          />
          
          <IonToast
            position="top"
            isOpen={showToast}
            message={errorLogin}
            duration={3000}
            color={status.error ? "danger" : "success"}
          />
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default LoanClearOff;